/**
 * CashPnlDashboard Component
 * Cash & P&L tab orchestrator: summary cards + collapsible detail sections
 */

import { MetricCard } from './MetricCard';
import { StatusBadge } from './StatusBadge';
import { BankAccountsSection } from './BankAccountsSection';
import { ExpensesCategorySection } from './ExpensesCategorySection';
import { WorkingCapitalSection } from './WorkingCapitalSection';
import { TopVendorsSection } from './TopVendorsSection';
import { CommentButton } from './CommentButton';
import { AIInsightSection } from './AIInsightSection';
import { useCashPosition } from '@/hooks/useCashPosition';
import { PageNotes } from './PageNotes';
import { formatCurrency } from '@/utils/formatters';
import './CashPnlDashboard.css';

function runwayStatus(status: string): 'healthy' | 'warning' | 'critical' {
  if (status === 'critical') return 'critical';
  if (status === 'warning') return 'warning';
  return 'healthy';
}

function runwayLabel(months: number | null | undefined): string {
  if (months == null || months >= 999) return 'No burn rate';
  if (months >= 12) return `${months.toFixed(0)} months runway`;
  if (months >= 1) return `${months.toFixed(1)} months runway`;
  return `${(months * 30).toFixed(0)} days runway`;
}

export function CashPnlDashboard() {
  const { data, loading, error, refresh } = useCashPosition();
  const cashPosition = data?.cashPosition ?? null;
  const pnl = data?.pnl ?? null;
  const expenses = data?.expenses ?? null;

  if (error) {
    return (
      <div className="dashboard-error" role="alert" aria-live="assertive">
        <div className="dashboard-error__icon">&#9888;</div>
        <h2 className="dashboard-error__title">Unable to load Cash & P&L</h2>
        <p className="dashboard-error__message">{error}</p>
        <button onClick={refresh} className="dashboard-error__button" aria-label="Retry">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="cash-pnl-dashboard">
      <header className="cash-pnl-dashboard__header">
        <div>
          <h1 className="cash-pnl-dashboard__title">Cash & P&L</h1>
          {cashPosition?.calculatedAt && (
            <p className="cash-pnl-dashboard__subtitle">
              Real-time financial position
            </p>
          )}
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="dashboard-header__refresh"
          aria-label="Refresh cash and P&L data"
        >
          <span className={loading ? 'loading-spinner' : ''}>&#8635;</span>
          Refresh
        </button>
      </header>

      {/* Summary Cards */}
      <div className="cash-pnl-dashboard__cards">
        <MetricCard
          title="Total Cash"
          value={formatCurrency(cashPosition?.totalCash ?? 0)}
          subtitle={cashPosition ? runwayLabel(cashPosition.runway?.monthsRemaining) : undefined}
          icon="&#128176;"
          loading={loading}
          variant={
            cashPosition?.runway.status === 'critical'
              ? 'danger'
              : cashPosition?.runway.status === 'warning'
                ? 'warning'
                : 'success'
          }
          headerAction={<CommentButton targetType="metric" targetId="total-cash" />}
        >
          {cashPosition && (
            <>
              <StatusBadge
                status={runwayStatus(cashPosition.runway?.status ?? 'healthy')}
                label={
                  cashPosition.runway?.monthsRemaining != null && cashPosition.runway.monthsRemaining < 999
                    ? `${cashPosition.runway.monthsRemaining.toFixed(1)} months`
                    : 'No burn rate'
                }
              />
              <CashBreakdown balances={cashPosition.bankBalances} />
            </>
          )}
        </MetricCard>

        <MetricCard
          title="Revenue"
          value={formatCurrency(pnl?.totalRevenue ?? 0)}
          subtitle={pnl ? `${pnl.fromDate} to ${pnl.toDate}` : undefined}
          icon="&#128200;"
          loading={loading}
          variant="success"
          headerAction={<CommentButton targetType="metric" targetId="pnl-revenue" />}
        />

        <MetricCard
          title="Net Profit"
          value={formatCurrency(pnl?.netProfit ?? 0)}
          subtitle={pnl ? `Gross: ${formatCurrency(pnl.grossProfit)}` : undefined}
          icon="&#128178;"
          loading={loading}
          variant={(pnl?.netProfit ?? 0) >= 0 ? 'success' : 'danger'}
          headerAction={<CommentButton targetType="metric" targetId="net-profit" />}
        />
      </div>

      {/* Collapsible Sections */}
      <div className="cash-pnl-dashboard__sections">
        {cashPosition && (
          <BankAccountsSection bankBalances={cashPosition.bankBalances} />
        )}
        {expenses && (
          <ExpensesCategorySection expenses={expenses} />
        )}
        {cashPosition && (
          <WorkingCapitalSection workingCapital={cashPosition.workingCapital} />
        )}
        {expenses && (
          <TopVendorsSection topVendors={expenses.topVendors} />
        )}
        <AIInsightSection
          id="cash-pnl"
          title="AI Analysis — Cash & P&L"
          question="Summarise our current cash position, runway, revenue and net profit. Highlight any risks or actions needed."
          contextHint="cash"
        />
        <PageNotes page="cash-pnl" title="Cash & P&L Notes" />
      </div>
    </div>
  );
}

function CashBreakdown({ balances }: { balances: Array<{ name: string; balance: number }> }) {
  const taxReserve = balances.filter((b) =>
    b.name.toLowerCase().includes('tax') || b.name.toLowerCase().includes('savings')
  );
  const operational = balances.filter((b) =>
    !b.name.toLowerCase().includes('tax') && !b.name.toLowerCase().includes('savings')
  );
  const opTotal = operational.reduce((s, b) => s + b.balance, 0);
  const resTotal = taxReserve.reduce((s, b) => s + b.balance, 0);

  if (resTotal === 0) return null;

  return (
    <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', borderTop: '1px solid #e8ecef', paddingTop: '6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Operational</span>
        <span style={{ fontWeight: 500 }}>{formatCurrency(opTotal)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Tax Reserve</span>
        <span style={{ fontWeight: 500 }}>{formatCurrency(resTotal)}</span>
      </div>
    </div>
  );
}
